/* Copyright (c) 2020-present CloudIO Inc */

import { ActionItemType } from '../../registry/Base.type';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  AliasType,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionQuery';
import { analyzeActionQuery } from './ActionQueryAnalyze';

const ActionQueryManager: ActionManagerFn<'ActionQuery'> = (
  platform: Platform,
): ActionManager<'ActionQuery'> => {
  return {
    ...baseActionManager(platform, 'ActionQuery'),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ActionSetAggregateAttribute',
          'ActionSetDistinct',
          'ActionSetFilterCombiner',
          'ActionSetGroupByAttribute',
          'ActionSetProjection',
          'ActionSetQueryFilter',
          'ActionSetSimpleQueryFilter',
          'ActionSetSort',
        ] as ActionItemType[]
      ).includes(childType as ActionItemType),
    // allow execution of query inside TriggerBeforeQuery
    // if copy filters exists
    isAllowedInsideParent: (page, parent, { appUid, pageId }) => {
      if (parent.type === 'TriggerBeforeQuery') {
        const copyFilters = platform.getChildrenOfTypeById(
          appUid,
          pageId,
          parent.itemId,
          'ActionCopyFilters',
        );
        return copyFilters.length > 0;
      }
      return true;
    },
    defaultChildren: ['ActionSetQueryFilter'],
    props: {
      ...baseActionProps,
      embeddedPageId: {
        label: 'Embedded Page ID',
        type: 'string',
      },
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        pageIdProp: 'embeddedPageId',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
      },
      force: {
        label: 'Force',
        type: 'boolean',
        helpText:
          'Force the query to be executed even if the filters are not changed',
      },
      applyFiltersToSmartSearch: {
        label: 'Apply Filters to Smart Search',
        type: 'boolean',
      },
      showEmptyFilterWarning: {
        label: 'Show Empty Filter Warning',
        type: 'boolean',
        helpText:
          'Enable this to know if empty value passed to filters(Recommended for developer).',
      },
    },
    name: 'Query',
    getDisplayText: (item) => item.props.alias,
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'QUERY_FILTER'
        ? { aliasProp: 'alias', embeddedPageIdProp: 'embeddedPageId' }
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    icon: { iconName: 'search' },
    analyze: analyzeActionQuery,
    description: `Use this action to programmatically invoke a read operation on a given Data Source.

You can use one or more of the child actions to further customize the query.

- 'Set Query Filter' to apply one or more filters including complex filters grouped with AND/OR clauses
- 'Set Projection' to optionally select only a few sets of attributes to include in the result
- 'Set Sort' to define the order by clause of the SQL query
- 'Set Distinct' to apply a DISTINCT keyword at the beginning of the select clause
- 'Set Group By Attribute' to apply GROUP BY clause
- 'Set Aggregate Attribute' to apply SQL aggregate functions such as  COUNT, SUM, MIN, MAX, or AVG.
`,
  };
};

export default ActionQueryManager;
